import React, { FC, useState } from 'react';
import { 
  Calculator,
  CheckCircle2,
  AlertCircle,
  Building,
  Search,
  CheckSquare,
  Calendar,
  TrendingUp,
  Clock,
  Coins,
  Check,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { Restaurant } from '../types';

interface PlansViewProps {
  restaurants: Restaurant[];
  onUpdateRestaurant: (id: string, updatedFields: Partial<Restaurant>) => Promise<void>;
}

export const PlansView: FC<PlansViewProps> = ({ 
  restaurants, 
  onUpdateRestaurant 
}) => {
  // Local states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'setup_unpaid' | 'monthly_overdue' | 'fully_paid'>('all');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isUpdatingId, setIsUpdatingId] = useState<string | null>(null);

  // Revenue simulator levels
  const [simulatedNewOnboards, setSimulatedNewOnboards] = useState(15);
  const [simulatedRegularSubscribers, setSimulatedRegularSubscribers] = useState(45);

  // Helper values to resolve default payment states dynamically if not explicitly set
  const getBillingStatus = (rest: any) => {
    const setupPaid = rest.setupPaid !== undefined ? rest.setupPaid : true;
    const monthlyPaid = rest.monthlyPaid !== undefined 
      ? rest.monthlyPaid 
      : (rest.status === 'active');
      
    const billingPeriod = rest.expiryDate 
      ? new Date(rest.expiryDate).toLocaleDateString('az-AZ', { month: 'long', year: 'numeric' })
      : 'Cari ay';

    const calculatedTotal = rest.totalPaidAZN !== undefined 
      ? rest.totalPaidAZN 
      : (setupPaid ? 300 : 0) + (monthlyPaid ? 70 : 0);

    return { setupPaid, monthlyPaid, billingPeriod, calculatedTotal };
  };

  const triggerToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // Toggle Setup Paid Status (300 AZN)
  const handleToggleSetupPaid = async (restaurantId: string, currentStatus: boolean) => {
    setIsUpdatingId(`${restaurantId}-setup`);
    const nextStatus = !currentStatus;
    
    const rest = restaurants.find(r => r.id === restaurantId);
    if (!rest) return;
    const info = getBillingStatus(rest);
    const nextTotal = (nextStatus ? 300 : 0) + (info.monthlyPaid ? 70 : 0);

    try {
      await onUpdateRestaurant(restaurantId, {
        ...({
          setupPaid: nextStatus,
          totalPaidAZN: nextTotal
        } as any)
      });
      triggerToast(`"${rest.name}" restoranının quraşdırma ödəniş statusu uğurla yeniləndi!`);
    } catch (e) {
      console.error(e);
    } finally {
      setIsUpdatingId(null);
    }
  };

  // Toggle Monthly Paid Status (70 AZN)
  const handleToggleMonthlyPaid = async (restaurantId: string, currentStatus: boolean) => {
    setIsUpdatingId(`${restaurantId}-monthly`);
    const nextStatus = !currentStatus;
    
    const rest = restaurants.find(r => r.id === restaurantId);
    if (!rest) return;
    const info = getBillingStatus(rest);
    const nextTotal = (info.setupPaid ? 300 : 0) + (nextStatus ? 70 : 0);

    try {
      await onUpdateRestaurant(restaurantId, {
        ...({
          monthlyPaid: nextStatus,
          totalPaidAZN: nextTotal,
          status: nextStatus ? 'active' : 'suspended'
        } as any)
      });
      triggerToast(`"${rest.name}" restoranının aylıq abunə ödəniş statusu uğurla tənzimləndi!`);
    } catch (e) {
      console.error(e);
    } finally {
      setIsUpdatingId(null);
    }
  };

  // Mark Everything as Settled and Active
  const handleSettleAll = async (restaurantId: string) => {
    setIsUpdatingId(`${restaurantId}-settle`);
    const rest = restaurants.find(r => r.id === restaurantId);
    if (!rest) return;

    try {
      await onUpdateRestaurant(restaurantId, {
        status: 'active',
        ...({
          setupPaid: true,
          monthlyPaid: true,
          totalPaidAZN: 370
        } as any)
      });
      triggerToast(`"${rest.name}" restoranının bütün borcları uğurla sıfırlandı! Hesab aktivdir.`);
    } catch (e) {
      console.error(e);
    } finally {
      setIsUpdatingId(null);
    }
  };

  // Metrics calculators
  const stats = (() => {
    let totalSetupCollected = 0;
    let totalMonthlyCollected = 0;
    let pendingSetupCount = 0;
    let pendingMonthlyCount = 0;

    restaurants.forEach(r => {
      const billing = getBillingStatus(r);
      if (billing.setupPaid) {
        totalSetupCollected += 300;
      } else {
        pendingSetupCount++;
      }

      if (billing.monthlyPaid) {
        totalMonthlyCollected += 70;
      } else {
        pendingMonthlyCount++;
      }
    });

    const totalCollected = totalSetupCollected + totalMonthlyCollected;
    const totalPending = (pendingSetupCount * 300) + (pendingMonthlyCount * 70);
    const expectedMRR = restaurants.filter(r => r.status === 'active').length * 70;

    return {
      totalCollected,
      totalPending,
      expectedMRR,
      totalSetupCollected,
      totalMonthlyCollected,
      pendingSetupCount,
      pendingMonthlyCount
    };
  })();

  // Filtered ledger list
  const filteredRestaurants = restaurants.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        r.ownerName.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchSearch) return false;

    const billing = getBillingStatus(r);
    if (filterType === 'setup_unpaid') return !billing.setupPaid;
    if (filterType === 'monthly_overdue') return !billing.monthlyPaid;
    if (filterType === 'fully_paid') return billing.setupPaid && billing.monthlyPaid;
    
    return true;
  });

  return (
    <div className="space-y-6 text-slate-800 animate-fade-in">
      
      {/* Title Header with Pricing Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-slate-950 flex items-center gap-2">
            💳 Maliyyə Hesabatlığı və Abunəliklər
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Rəqəmsal menyu xidmətləri üzrə əməliyyatların mərkəzləşdirilmiş auditi. Sabit tariflər: Quraşdırma üçün bir dəfəlik <span className="font-bold text-indigo-650 font-mono">300 AZN</span> və aylıq abunə haqqı <span className="font-bold text-indigo-650 font-mono">70 AZN</span>.
          </p>
        </div>
        
        <div className="flex gap-2.5 flex-wrap">
          <span className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 px-3 py-1.5 rounded-xl font-mono text-[11px] font-bold select-none shadow-xs">
            <Coins className="w-3.5 h-3.5" />
            Quraşdırma: 300 AZN (Birdəfəlik)
          </span>
          <span className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-250 text-emerald-700 px-3 py-1.5 rounded-xl font-mono text-[11px] font-bold select-none shadow-xs">
            <Calendar className="w-3.5 h-3.5" />
            Aylıq abunə: 70 AZN / restoran
          </span>
        </div>
      </div>

      {/* SUCCESS POPUP TOAST */}
      {successMsg && (
        <div className="p-4 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold leading-none animate-bounce flex items-center gap-2">
          <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 flex-shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* CORE FINANCIAL OVERVIEW CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Collected Revenue */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="text-[10px] text-slate-400 font-mono font-bold uppercase tracking-wider block">
              Mədaxil edilmiş ümumi məbləğ
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-slate-950 font-mono">
                {stats.totalCollected.toLocaleString()}
              </span>
              <span className="text-[11px] text-slate-550 font-bold font-mono">AZN</span>
            </div>
            <div className="text-[10px] text-slate-400 font-mono font-semibold">
              Quraşdırma: {stats.totalSetupCollected} AZN • Abunəlik: {stats.totalMonthlyCollected} AZN
            </div>
          </div>
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl shrink-0 shadow-xs">
            <Coins className="w-6 h-6" />
          </div>
        </div>

        {/* Current Active MRR projection */}
        <div className="bg-slate-900 p-5 rounded-2xl text-white shadow-md flex items-center justify-between gap-4 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />
          <div className="space-y-1 relative z-10">
            <span className="text-[10px] text-indigo-300 font-mono font-bold uppercase tracking-wider block">
              Aylıq təkrarlanan gəlir (MRR)
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-white font-mono">
                {stats.expectedMRR.toLocaleString()}
              </span>
              <span className="text-[11px] text-indigo-300 font-bold font-mono">AZN</span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              Aktiv restoran sayı: {restaurants.filter(r => r.status === 'active').length}
            </p>
          </div>
          <div className="p-3 bg-slate-800 text-indigo-400 rounded-2xl shrink-0 border border-slate-700/50 shadow-xs">
            <TrendingUp className="w-6 h-6" />
          </div>
        </div>

        {/* Expected/Pending Bills */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="text-[10px] text-amber-600 font-mono font-bold uppercase tracking-wider block">
              Gözlənilən ödənişlər (Debitor)
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-amber-500 font-mono">
                {stats.totalPending.toLocaleString()}
              </span>
              <span className="text-[11px] text-slate-500 font-bold font-mono">AZN</span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              Ödənişi gecikənlər: {restaurants.filter(r => !getBillingStatus(r).setupPaid || !getBillingStatus(r).monthlyPaid).length} restoran
            </p>
          </div>
          <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl shrink-0 shadow-xs">
            <Clock className="w-6 h-6 animate-pulse" />
          </div>
        </div>

        {/* Total Registered Restaurants count */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="text-[10px] text-slate-400 font-mono font-bold uppercase tracking-wider block">
              CRM-də qeydə alınmış restoranlar
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-slate-900 font-mono">
                {restaurants.length}
              </span>
              <span className="text-[11px] text-slate-500 font-bold">restoran</span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">
              Sınama / Dondurulmuş mərhələ: {restaurants.filter(r => r.status !== 'active').length}
            </p>
          </div>
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl shrink-0 shadow-xs">
            <Building className="w-6 h-6" />
          </div>
        </div>

      </div>

      {/* SEARCH AND BILLING LEDGER MODULE */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        
        {/* Ledger Toolbar */}
        <div className="p-5 border-b border-slate-150 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
          
          {/* Quick status tabs filter */}
          <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl overflow-x-auto self-start border border-slate-200">
            <button
              type="button"
              onClick={() => setFilterType('all')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap outline-hidden ${
                filterType === 'all' 
                  ? 'bg-white text-indigo-700 shadow-xs font-black' 
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Bütün restoranlar ({restaurants.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterType('setup_unpaid')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap outline-hidden ${
                filterType === 'setup_unpaid' 
                  ? 'bg-white text-rose-750 shadow-xs font-black' 
                  : 'text-slate-505 hover:text-slate-800'
              }`}
            >
              Quraşdırma borcu (300 AZN) • {restaurants.filter(r => !getBillingStatus(r).setupPaid).length}
            </button>
            <button
              type="button"
              onClick={() => setFilterType('monthly_overdue')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap outline-hidden ${
                filterType === 'monthly_overdue' 
                  ? 'bg-white text-amber-700 shadow-xs font-black' 
                  : 'text-slate-505 hover:text-slate-800'
              }`}
            >
              Abunə borcu (70 AZN) • {restaurants.filter(r => !getBillingStatus(r).monthlyPaid).length}
            </button>
            <button
              type="button"
              onClick={() => setFilterType('fully_paid')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap outline-hidden ${
                filterType === 'fully_paid' 
                  ? 'bg-white text-emerald-700 shadow-xs font-black' 
                  : 'text-slate-505 hover:text-slate-800'
              }`}
            >
              Tamamilə ödənilib • {restaurants.filter(r => {
                const b = getBillingStatus(r);
                return b.setupPaid && b.monthlyPaid;
              }).length}
            </button>
          </div>

          {/* Search ledger */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Restoran və ya sahibkarın adı üzrə axtarış..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:border-indigo-400 placeholder-slate-400"
            />
          </div>

        </div>

        {/* Ledger Table Desktop / Card layout */}
        {filteredRestaurants.length === 0 ? (
          <div className="py-12 text-center space-y-2 max-w-sm mx-auto">
            <AlertCircle className="w-10 h-10 text-slate-300 mx-auto animate-pulse" />
            <h4 className="font-bold text-sm text-slate-705">Heç bir məlumat tapılmadı</h4>
            <p className="text-xs text-slate-400">
              Axtardığınız sözə və ya seçilmiş ödəniş statusuna uyğun restoran reyestrdə aşkar edilmədi.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto min-w-full">
            <table className="w-full border-collapse text-left text-xs text-slate-600 min-w-[900px]">
              <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-mono text-[10px] uppercase font-bold tracking-wider">
                <tr>
                  <th className="py-3.5 px-6">Restoran və Sahibkar</th>
                  <th className="py-3.5 px-4 text-center">Quraşdırma ödənişi (300 AZN)</th>
                  <th className="py-3.5 px-4 text-center">Aylıq abunə haqqı (70 AZN)</th>
                  <th className="py-3.5 px-4 text-center">Cəmi Ödənilib</th>
                  <th className="py-3.5 px-4">Hesabat dövrü</th>
                  <th className="py-3.5 px-4 text-right">Şəbəkə statusu</th>
                  <th className="py-3.5 px-6 text-right">Sürətli əməliyyat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRestaurants.map(rest => {
                  const billingStatus = getBillingStatus(rest);
                  const isUpdatingSetup = isUpdatingId === `${rest.id}-setup`;
                  const isUpdatingMonthly = isUpdatingId === `${rest.id}-monthly`;
                  const isSettling = isUpdatingId === `${rest.id}-settle`;

                  return (
                    <tr key={rest.id} className="hover:bg-slate-50/50 transition-colors">
                      
                      {/* Name & Contact Info */}
                      <td className="py-4 px-6">
                        <div className="space-y-0.5">
                          <p className="font-extrabold text-slate-400-none text-slate-900 text-sm leading-tight">{rest.name}</p>
                          <p className="text-[10px] text-slate-400 font-medium">
                            {rest.ownerName} • <span className="text-indigo-600 font-mono font-black">{rest.ownerPhone}</span>
                          </p>
                          <p className="text-[10px] text-slate-400 truncate max-w-[200px]" title={rest.ownerEmail}>
                            ✉️ {rest.ownerEmail}
                          </p>
                        </div>
                      </td>

                      {/* Setup Payment 300 AZN Toggle */}
                      <td className="py-4 px-4 text-center">
                        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            disabled={isUpdatingSetup}
                            onClick={() => handleToggleSetupPaid(rest.id, billingStatus.setupPaid)}
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-black font-mono flex items-center gap-1.5 border transition-all cursor-pointer select-none ${
                              billingStatus.setupPaid 
                                ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600 shadow-xs' 
                                : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 animate-pulse'
                            }`}
                          >
                            {isUpdatingSetup ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : billingStatus.setupPaid ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                              <AlertCircle className="w-3.5 h-3.5" />
                            )}
                            <span>{billingStatus.setupPaid ? '300 AZN Ödənilib' : '300 AZN Gözlənilir'}</span>
                          </button>
                        </div>
                      </td>

                      {/* Monthly Subscription 70 AZN Toggle */}
                      <td className="py-4 px-4 text-center">
                        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            disabled={isUpdatingMonthly}
                            onClick={() => handleToggleMonthlyPaid(rest.id, billingStatus.monthlyPaid)}
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-black font-mono flex items-center gap-1.5 border transition-all cursor-pointer select-none ${
                              billingStatus.monthlyPaid 
                                ? 'bg-indigo-650 text-white border-indigo-700 hover:bg-indigo-750 shadow-xs' 
                                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 animate-pulse'
                            }`}
                          >
                            {isUpdatingMonthly ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : billingStatus.monthlyPaid ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                              <AlertCircle className="w-3.5 h-3.5" />
                            )}
                            <span>{billingStatus.monthlyPaid ? '70 AZN Ödənilib' : '70 AZN Borc'}</span>
                          </button>
                        </div>
                      </td>

                      {/* Total Paid Accumulator */}
                      <td className="py-4 px-4 text-center font-bold font-mono text-sm text-slate-800">
                        {billingStatus.calculatedTotal} AZN
                      </td>

                      {/* Current active period */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5 text-slate-500 font-mono text-[11px] select-none">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          <span>{billingStatus.billingPeriod}</span>
                        </div>
                      </td>

                      {/* Connection status tag */}
                      <td className="py-4 px-4 text-right">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-extrabold font-mono tracking-wide uppercase select-none ${
                          rest.status === 'active' 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-150'
                            : rest.status === 'trial'
                            ? 'bg-blue-50 text-blue-700 border border-blue-150'
                            : 'bg-rose-50 text-rose-700 border border-rose-150'
                        }`}>
                          {rest.status === 'active' ? 'Aktivdir' : rest.status === 'trial' ? 'Sınaq mərhələsi' : 'Dondurulub'}
                        </span>
                      </td>

                      {/* Quick settle all button options */}
                      <td className="py-4 px-6 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {(!billingStatus.setupPaid || !billingStatus.monthlyPaid) ? (
                            <button
                              type="button"
                              disabled={isSettling}
                              onClick={() => handleSettleAll(rest.id)}
                              className="px-2.5 py-1 bg-slate-900 text-white hover:bg-slate-800 border border-slate-900 rounded font-bold hover:shadow-sm text-[10px] transition-all cursor-pointer flex items-center gap-1 select-none"
                            >
                              {isSettling ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckSquare className="w-3 h-3" />
                              )}
                              <span>Borcları sıfırla</span>
                            </button>
                          ) : (
                            <span className="text-[10px] text-emerald-600 font-extrabold font-mono flex items-center gap-1 select-none">
                              <Check className="w-3.5 h-3.5" /> Tam Hesablaşma
                            </span>
                          )}
                        </div>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* DYNAMIC METRIC INTERACTIVE SIMULATOR FOR PROJECTIONS */}
      <div className="bg-slate-950 text-white rounded-2xl p-6 relative border border-slate-900 shadow-xl overflow-hidden">
        
        <div className="absolute right-0 top-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-1/4 bottom-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />

        {/* Title widget */}
        <div className="flex items-center gap-2 border-b border-slate-900 pb-3 mb-5">
          <Calculator className="w-5 h-5 text-emerald-400" />
          <div>
            <h3 className="font-bold text-base text-white">
              Gəlirlilik və Müştəri Artımı Kalkulyatoru
            </h3>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              Biznesinizi böyütdükcə bir dəfəlik quraşdırma gəlirlərini (300 AZN) və aylıq təkrarlanan mənfəətlərinizi (70 AZN) asanlıqla proqnozlaşdırın.
            </p>
          </div>
        </div>

        {/* Dynamic Sliders grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
          
          {/* Slider 1: Onboardings */}
          <div className="space-y-2 bg-slate-900/50 p-4 rounded-xl border border-slate-800/85">
            <div className="flex justify-between text-xs">
              <span className="font-bold text-slate-300">Planlaşdırılan quraşdırma (aylıq yeni qoşulma):</span>
              <span className="font-mono text-emerald-400 font-extrabold">{simulatedNewOnboards} restoran ({simulatedNewOnboards * 300} AZN)</span>
            </div>
            <input 
              type="range"
              min="0"
              max="100"
              value={simulatedNewOnboards}
              onChange={(e) => setSimulatedNewOnboards(Number(e.target.value))}
              className="w-full accent-indigo-500 h-1.5 bg-slate-800 rounded-lg cursor-pointer"
            />
            <p className="text-[10px] text-slate-500 italic">Yeni müəssisənin sistemə daxil edilməsi və çəkiliş xidməti daxil olmaqla birdəfəlik quraşdırma haqqı (hər birinə 300 AZN).</p>
          </div>

          {/* Slider 2: Regular subscribers */}
          <div className="space-y-2 bg-slate-900/50 p-4 rounded-xl border border-slate-800/85">
            <div className="flex justify-between text-xs">
              <span className="font-bold text-slate-300">Aylıq abunə haqqı ödəyən ümumi restoran sayı:</span>
              <span className="font-mono text-indigo-400 font-extrabold">{simulatedRegularSubscribers} restoran ({simulatedRegularSubscribers * 70} AZN / ay)</span>
            </div>
            <input 
              type="range"
              min="0"
              max="250"
              value={simulatedRegularSubscribers}
              onChange={(e) => setSimulatedRegularSubscribers(Number(e.target.value))}
              className="w-full accent-emerald-400 h-1.5 bg-slate-800 rounded-lg cursor-pointer"
            />
            <p className="text-[10px] text-slate-500 italic">Menyu videolarının serverdə saxlanılması və QR dəstəyi üçün aylıq təkrarlanan sabit abunə haqqı (hər birinə 70 AZN).</p>
          </div>

        </div>

        {/* Projection results breakdown */}
        <div className="bg-slate-900/40 p-5 rounded-xl border border-slate-900/80 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <div className="space-y-1">
            <span className="text-[10px] font-mono uppercase text-slate-400 font-bold block tracking-wider">
              Simulyasiya üzrə ümumi aylıq gəlir:
            </span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-black text-emerald-400 font-mono">
                {((simulatedNewOnboards * 300) + (simulatedRegularSubscribers * 70)).toLocaleString()} AZN
              </span>
              <span className="text-xs text-slate-400 font-mono">/ ayda ümumi dövriyyə</span>
            </div>
          </div>

          <div className="text-[11px] font-mono text-slate-400 space-y-1">
            <div className="flex justify-between gap-5 text-slate-300">
              <span>Birdəfəlik quraşdırma gəlirləri:</span>
              <span className="font-bold text-emerald-400">{(simulatedNewOnboards * 300).toLocaleString()} AZN</span>
            </div>
            <div className="flex justify-between gap-5 text-slate-300">
              <span>Aylıq təkrarlanan ümumi sabit gəlir (MRR):</span>
              <span className="font-bold text-indigo-400">{(simulatedRegularSubscribers * 70).toLocaleString()} AZN</span>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};
